import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  FileText, FileSpreadsheet, Upload, CheckCircle, XCircle,
  Eye, Save, RotateCcw, ChevronDown, MapPin, List,
  AlertTriangle, ChevronLeft, ChevronRight,
  Search, Edit3, Trash2, X, RefreshCw, ChevronUp, Download,
} from 'lucide-react';
import { filesApi, geodesieApi } from '../../api/client';

const MEDIA_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* geodesieApi renvoie une Feature GeoJSON par point (coordonnées WGS84 +
   laborde imbriquées) — cette page travaille sur des objets plats. */
function normalizeGeoPoint(feature) {
  const [longitude, latitude] = feature.geometry?.coordinates || [];
  const [x_laborde, y_laborde] = feature.properties?.laborde?.coordinates || [];
  return {
    id: feature.id,
    nom: feature.properties?.nom,
    id_point: feature.properties?.id_point,
    ordre: feature.properties?.ordre,
    precision: feature.properties?.precision,
    pdf_file: feature.properties?.pdf_file,
    date_ajout: feature.properties?.date_ajout,
    latitude, longitude, x_laborde, y_laborde,
  };
}

/* ── Palette — références CSS vars (thème dynamique) ──────────── */
const C = {
  bg:       'var(--c-bg)',
  surface:  'var(--c-surface)',
  surface2: 'var(--c-surface2)',
  border:   'var(--c-border)',
  borderDk: 'var(--c-border-dk)',
  green:    'var(--c-accent)',
  amber:    'var(--c-amber)',
  red:      'var(--c-red)',
  text:     'var(--c-text)',
  muted:    'var(--c-muted)',
  dim:      'var(--c-dim)',
  greenGlow:'var(--c-glow)',
  overlay:  'var(--c-overlay)',
  bg60:     'var(--c-bg-60)',
  bg80:     'var(--c-bg-80)',
  a04: 'var(--c-a04)', a05: 'var(--c-a05)', a06: 'var(--c-a06)',
  a07: 'var(--c-a07)', a08: 'var(--c-a08)', a10: 'var(--c-a10)',
  a12: 'var(--c-a12)', a14: 'var(--c-a14)', a15: 'var(--c-a15)',
  a20: 'var(--c-a20)', a25: 'var(--c-a25)', a28: 'var(--c-a28)',
  a30: 'var(--c-a30)', a35: 'var(--c-a35)', a40: 'var(--c-a40)',
};

const TABS = [
  { id: 'pdf',       label: 'PDF / Word',   icon: FileText },
  { id: 'excel',     label: 'Excel / CSV',  icon: FileSpreadsheet },
  { id: 'inventory', label: 'Inventaire',   icon: List },
];

/* ── Atoms ───────────────────────────────────────────────────── */
function Btn({ onClick, disabled, variant = 'primary', children, style }) {
  const themes = {
    primary: { background: C.green,         color: C.bg },
    ghost:   { background: 'transparent',    border: `1px solid ${C.borderDk}`, color: C.text },
    danger:  { background: C.red,            color: '#fff' },
    amber:   { background: 'rgba(245,158,11,.15)', border: `1px solid rgba(245,158,11,.4)`, color: C.amber },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', border: 'none',
        transition: 'all .15s', opacity: disabled ? .5 : 1,
        ...themes[variant], ...style,
      }}
    >{children}</button>
  );
}

function Alert({ type, children }) {
  const cfg = {
    success: { bg: C.a10, border: C.a30,                    icon: CheckCircle,  color: C.green },
    error:   { bg: 'rgba(239,68,68,.1)',  border: 'rgba(239,68,68,.3)',  icon: XCircle,      color: C.red   },
    warning: { bg: 'rgba(245,158,11,.1)', border: 'rgba(245,158,11,.3)', icon: AlertTriangle,color: C.amber },
  }[type] || {};
  const Icon = cfg.icon;
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.text,
    }}>
      {Icon && <Icon size={15} color={cfg.color} style={{ flexShrink: 0, marginTop: 1 }} />}
      <div>{children}</div>
    </div>
  );
}

function DropZone({ accept, onFile, label }) {
  const ref  = useRef();
  const [drag, setDrag] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => ref.current?.click()}
      style={{
        border: `2px dashed ${drag ? C.green : C.border}`,
        borderRadius: 12, padding: '40px 24px', textAlign: 'center',
        cursor: 'pointer', background: drag ? C.greenGlow : 'transparent',
        transition: 'all .2s',
      }}
    >
      <Upload size={28} color={drag ? C.green : C.muted} style={{ marginBottom: 10 }} />
      <div style={{ color: C.text, fontSize: 14, marginBottom: 4 }}>{label}</div>
      <div style={{ color: C.muted, fontSize: 12 }}>Glisser-déposer ou cliquer pour sélectionner</div>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files[0]; if (f) onFile(f); }} />
    </div>
  );
}

const ZOOM_LEVELS = [50, 75, 100, 125, 150, 175, 200];

/* ── PDF Viewer panel ────────────────────────────────────────── */
function PdfViewer({ fileUrl, page, totalPages, onPageChange }) {
  const [key,  setKey]  = useState(0);
  const [zoom, setZoom] = useState(100);

  const zoomIdx  = ZOOM_LEVELS.indexOf(zoom);
  const canZoomOut = zoomIdx > 0;
  const canZoomIn  = zoomIdx < ZOOM_LEVELS.length - 1;

  // Force iframe reload when page OR zoom changes
  useEffect(() => { setKey(k => k + 1); }, [page, zoom]);

  const zoomBtn = (label, action, enabled) => (
    <button onClick={enabled ? action : undefined} style={{
      background: 'none', border: 'none', padding: '2px 6px',
      color: enabled ? C.text : C.dim,
      cursor: enabled ? 'pointer' : 'default',
      fontSize: 16, lineHeight: 1, borderRadius: 4,
    }}>{label}</button>
  );

  if (!fileUrl) return null;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: C.surface2, borderRadius: 10, border: `1px solid ${C.border}`,
      overflow: 'hidden',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
        borderBottom: `1px solid ${C.border}`, background: C.surface,
        flexShrink: 0,
      }}>
        <Eye size={13} color={C.green} />
        <span style={{ fontSize: 12, color: C.muted }}>
          Page {page} / {totalPages}
        </span>

        {/* Séparateur */}
        <div style={{ flex: 1 }} />

        {/* Contrôles zoom */}
        {zoomBtn('−', () => setZoom(ZOOM_LEVELS[zoomIdx - 1]), canZoomOut)}
        <button
          onClick={() => setZoom(100)}
          title="Réinitialiser le zoom"
          style={{
            background: zoom !== 100 ? C.a15 : 'transparent',
            border: `1px solid ${zoom !== 100 ? C.a35 : C.border}`,
            borderRadius: 5, padding: '1px 8px', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
            color: zoom !== 100 ? C.green : C.muted, minWidth: 42, textAlign: 'center',
          }}
        >{zoom}%</button>
        {zoomBtn('+', () => setZoom(ZOOM_LEVELS[zoomIdx + 1]), canZoomIn)}

        {/* Séparateur */}
        <div style={{ width: 1, height: 16, background: C.border, margin: '0 4px' }} />

        {/* Navigation pages */}
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          style={{ background: 'none', border: 'none', color: page > 1 ? C.text : C.dim, cursor: page > 1 ? 'pointer' : 'default', padding: 4 }}
        >
          <ChevronLeft size={14} />
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          style={{ background: 'none', border: 'none', color: page < totalPages ? C.text : C.dim, cursor: page < totalPages ? 'pointer' : 'default', padding: 4 }}
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* PDF embedded — scrollbar visible quand zoomé */}
      <iframe
        key={key}
        src={`${fileUrl}#page=${page}&toolbar=0&navpanes=0&zoom=${zoom}`}
        style={{ flex: 1, border: 'none', background: '#fff' }}
        title={`Fiche géodésique page ${page}`}
      />
    </div>
  );
}

/* ── Points table with status ────────────────────────────────── */
function PointsTable({ points, selectedIdx, onSelect }) {
  const cols = [
    { key: 'status',    label: '',           w: 36  },
    { key: 'nom',       label: 'Nom',        w: 150 },
    { key: 'code',      label: 'Code',       w: 90  },
    { key: 'ordre',     label: 'Ordre',      w: 55  },
    { key: 'x_laborde', label: 'X Laborde',  w: 105 },
    { key: 'y_laborde', label: 'Y Laborde',  w: 112 },
    { key: 'latitude',  label: 'Latitude',   w: 100 },
    { key: 'longitude', label: 'Longitude',  w: 100 },
    { key: 'commune',   label: 'Commune',    w: 130 },
  ];

  return (
    <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${C.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.a07 }}>
            {cols.map(c => (
              <th key={c.key} style={{
                padding: '7px 8px', textAlign: 'left', color: C.muted,
                fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
                borderBottom: `1px solid ${C.border}`, minWidth: c.w,
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {points.map((p, i) => {
            const complete = p.coords_complete;
            const active   = i === selectedIdx;
            return (
              <tr
                key={i}
                onClick={() => onSelect(i)}
                style={{
                  borderBottom: `1px solid ${C.border}`,
                  cursor: 'pointer',
                  background: active
                    ? C.a12
                    : !complete ? 'rgba(245,158,11,0.04)' : 'transparent',
                  outline: active ? `1px solid ${C.green}` : 'none',
                }}
              >
                {/* Status icon */}
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  {complete
                    ? <CheckCircle size={13} color={C.green} />
                    : <AlertTriangle size={13} color={C.amber} />
                  }
                </td>

                {/* Nom */}
                <td style={{ padding: '6px 8px', color: C.text }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MapPin size={10} color={complete ? C.green : C.amber} />
                    <strong style={{ fontSize: 12 }}>{p.nom || '—'}</strong>
                  </span>
                </td>

                {/* Code */}
                <td style={{ padding: '6px 8px', color: C.muted, fontSize: 11 }}>{p.code || '—'}</td>

                {/* Ordre */}
                <td style={{ padding: '6px 8px' }}>
                  {p.ordre != null
                    ? <span style={{ background: C.a15, color: C.green, borderRadius: 5, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>R{p.ordre}</span>
                    : <span style={{ color: C.dim }}>—</span>}
                </td>

                {/* X Laborde */}
                <td style={{ padding: '6px 8px', color: p.x_laborde != null ? C.text : C.dim, fontFamily: 'monospace', fontSize: 11 }}>
                  {p.x_laborde != null ? p.x_laborde.toFixed(2) : '—'}
                </td>

                {/* Y Laborde */}
                <td style={{ padding: '6px 8px', color: p.y_laborde != null ? C.text : C.dim, fontFamily: 'monospace', fontSize: 11 }}>
                  {p.y_laborde != null ? p.y_laborde.toFixed(2) : '—'}
                </td>

                {/* Latitude */}
                <td style={{ padding: '6px 8px', color: p.latitude != null ? C.text : C.amber, fontFamily: 'monospace', fontSize: 11 }}>
                  {p.latitude != null ? p.latitude.toFixed(6) : <span style={{ color: C.amber }}>manquant</span>}
                </td>

                {/* Longitude */}
                <td style={{ padding: '6px 8px', color: p.longitude != null ? C.text : C.amber, fontFamily: 'monospace', fontSize: 11 }}>
                  {p.longitude != null ? p.longitude.toFixed(6) : <span style={{ color: C.amber }}>manquant</span>}
                </td>

                {/* Commune */}
                <td style={{ padding: '6px 8px', color: C.muted, fontSize: 11 }}>{p.commune || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── PDF Tab ─────────────────────────────────────────────────── */
function PdfTab({ onImportDone }) {
  const [file,       setFile]       = useState(null);
  const [fileUrl,    setFileUrl]    = useState(null);
  const [status,     setStatus]     = useState('idle');
  const [points,     setPoints]     = useState([]);
  const [selectedIdx,setSelected]   = useState(0);
  const [saved,      setSaved]      = useState(0);
  const [saveErrors, setSaveErrors] = useState([]);
  const [message,    setMessage]    = useState('');
  const [totalPages, setTotalPages] = useState(1);

  // Cleanup object URL on unmount/reset
  useEffect(() => () => { if (fileUrl) URL.revokeObjectURL(fileUrl); }, [fileUrl]);

  const reset = useCallback(() => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFile(null); setFileUrl(null); setStatus('idle');
    setPoints([]); setSelected(0); setSaved(0);
    setSaveErrors([]); setMessage('');
  }, [fileUrl]);

  const handleFile = useCallback((f) => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFile(f);
    setFileUrl(URL.createObjectURL(f));
    setStatus('idle'); setPoints([]);
  }, [fileUrl]);

  const extract = async () => {
    if (!file) return;
    setStatus('extracting');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('save', 'false');
      const { data } = await filesApi.pdfExtract(fd);
      const pts = data.data?.extracted || [];
      setPoints(pts);
      setTotalPages(pts.length > 0 ? Math.max(...pts.map(p => p.pdf_page || 1)) : 1);
      setSelected(0);
      setStatus('preview');
    } catch (e) {
      setMessage(e.response?.data?.message || "Erreur lors de l'extraction.");
      setStatus('error');
    }
  };

  const save = async () => {
    if (!file) return;
    setStatus('saving');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('save', 'true');
      const { data } = await filesApi.pdfExtract(fd);
      setSaved(data.data?.saved || 0);
      setSaveErrors(data.data?.errors || []);
      setMessage(data.message || '');
      setStatus('done');
      if ((data.data?.saved || 0) > 0) onImportDone?.();
    } catch (e) {
      setMessage(e.response?.data?.message || "Erreur lors de l'enregistrement.");
      setStatus('error');
    }
  };

  const busy          = status === 'extracting' || status === 'saving';
  const completeCount = points.filter(p => p.coords_complete).length;
  const selectedPoint = points[selectedIdx];
  const currentPage   = selectedPoint?.pdf_page || 1;

  /* ── Rendu ── */
  if (status === 'idle' || status === 'error') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DropZone
        accept=".pdf,.docx,.doc"
        onFile={handleFile}
        label={file ? file.name : 'Déposer une fiche géodésique FTM (.pdf ou .docx)'}
      />
      {file && status !== 'error' && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Réinitialiser</Btn>
          <Btn onClick={extract}><Eye size={14} /> Extraire et prévisualiser</Btn>
        </div>
      )}
      {status === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Alert type="error">{message}</Alert>
          <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Réessayer</Btn>
        </div>
      )}
    </div>
  );

  if (busy) return (
    <div style={{ textAlign: 'center', padding: '56px 0', color: C.muted }}>
      <div style={{ fontSize: 13, marginBottom: 10 }}>
        {status === 'extracting' ? 'Extraction en cours…' : 'Enregistrement en cours…'}
      </div>
      <div style={{ width: 200, height: 3, background: C.dim, borderRadius: 3, margin: '0 auto', overflow: 'hidden' }}>
        <div style={{ width: '55%', height: '100%', background: C.green, borderRadius: 3,
          animation: 'ag-slide 1.4s ease-in-out infinite' }} />
      </div>
    </div>
  );

  if (status === 'done') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Alert type="success">{message}</Alert>
      {saveErrors.map((e, i) => <Alert key={i} type="warning">{e}</Alert>)}
      <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Importer un autre fichier</Btn>
    </div>
  );

  /* ── Preview ── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Barre de résumé + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>
          {points.length} fiche(s) détectée(s)
        </div>
        <div style={{
          background: C.a12, border: `1px solid ${C.a30}`,
          color: C.green, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
        }}>
          {completeCount} complètes
        </div>
        {points.length - completeCount > 0 && (
          <div style={{
            background: 'rgba(245,158,11,.12)', border: `1px solid rgba(245,158,11,.3)`,
            color: C.amber, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
          }}>
            {points.length - completeCount} incomplètes (seront ignorées)
          </div>
        )}
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Annuler</Btn>
        {completeCount > 0 && (
          <Btn onClick={save}><Save size={14} /> Enregistrer {completeCount} point(s)</Btn>
        )}
      </div>

      {/* Légende */}
      {points.length - completeCount > 0 && (
        <Alert type="warning">
          Les fiches <strong style={{ color: C.amber }}>⚠</strong> n'ont pas les coordonnées dans les deux systèmes (Laborde et WGS84). Elles ne seront pas enregistrées.
        </Alert>
      )}

      {/* Split: table + PDF viewer */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 16, minHeight: 500 }}>

        {/* Table */}
        <div style={{ overflow: 'auto' }}>
          <PointsTable
            points={points}
            selectedIdx={selectedIdx}
            onSelect={setSelected}
          />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8, paddingLeft: 4 }}>
            Cliquer sur une ligne pour afficher la fiche PDF correspondante →
          </div>
        </div>

        {/* PDF Viewer */}
        <div style={{ minHeight: 500 }}>
          {fileUrl && file?.name?.endsWith('.pdf') ? (
            <PdfViewer
              fileUrl={fileUrl}
              page={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => {
                const idx = points.findIndex(pt => pt.pdf_page === p);
                if (idx >= 0) setSelected(idx);
              }}
            />
          ) : (
            <div style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: C.surface2, borderRadius: 10, border: `1px solid ${C.border}`,
              color: C.muted, fontSize: 13,
            }}>
              <FileText size={20} style={{ marginRight: 8 }} />
              Aperçu disponible uniquement pour les fichiers PDF
            </div>
          )}
        </div>
      </div>

      {/* Détail du point sélectionné */}
      {selectedPoint && (
        <div style={{
          background: C.surface2, borderRadius: 10, border: `1px solid ${C.borderDk}`,
          padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {/* Ligne 1 — identité + admin */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Detail label="Nom"       value={selectedPoint.nom} />
            <Detail label="Code"      value={selectedPoint.code} />
            <Detail label="Ordre"     value={selectedPoint.ordre != null ? `R${selectedPoint.ordre}` : null} />
            <Detail label="District"  value={selectedPoint.district} />
            <Detail label="Commune"   value={selectedPoint.commune} />
            <Detail label="Fokontany" value={selectedPoint.fokontany} />
          </div>

          <div style={{ borderTop: `1px solid ${C.border}` }} />

          {/* Ligne 2 — Laborde */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 60 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 4, letterSpacing: '0.05em' }}>LABORDE</div>
            </div>
            <Detail label="X"     value={selectedPoint.x_laborde?.toFixed(2)} mono />
            <Detail label="Y"     value={selectedPoint.y_laborde?.toFixed(2)} mono />
            <Detail label="Z NGM" value={selectedPoint.z_ngm?.toFixed(3)} mono />
            <Detail label="He (m)" value={selectedPoint.he?.toFixed(3)} mono />
          </div>

          <div style={{ borderTop: `1px solid ${C.border}` }} />

          {/* Ligne 3 — WGS84 décimal + DMS */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 60 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 4, letterSpacing: '0.05em' }}>WGS 84</div>
            </div>

            {/* Latitude */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 4 }}>Latitude (DD)</div>
              <div style={{
                fontSize: 13, fontFamily: 'monospace',
                color: selectedPoint.latitude != null ? C.text : C.amber,
              }}>
                {selectedPoint.latitude != null ? selectedPoint.latitude.toFixed(6) : <span style={{ color: C.amber }}>manquant</span>}
              </div>
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: C.green, marginTop: 3 }}>
                {decimalToDMS(selectedPoint.latitude, true) ?? '—'}
              </div>
            </div>

            {/* Longitude */}
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 4 }}>Longitude (DD)</div>
              <div style={{
                fontSize: 13, fontFamily: 'monospace',
                color: selectedPoint.longitude != null ? C.text : C.amber,
              }}>
                {selectedPoint.longitude != null ? selectedPoint.longitude.toFixed(6) : <span style={{ color: C.amber }}>manquant</span>}
              </div>
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: C.green, marginTop: 3 }}>
                {decimalToDMS(selectedPoint.longitude, false) ?? '—'}
              </div>
            </div>

            {/* Combiné DMS — à comparer visuellement avec le PDF */}
            {(selectedPoint.latitude != null && selectedPoint.longitude != null) && (
              <div style={{ marginLeft: 8 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 4 }}>DMS (vérification PDF)</div>
                <div style={{
                  fontFamily: 'monospace', fontSize: 12,
                  background: C.a08, border: `1px solid ${C.a25}`,
                  borderRadius: 6, padding: '5px 10px', color: C.text, whiteSpace: 'nowrap',
                }}>
                  {decimalToDMS(selectedPoint.latitude, true)}
                  {'  '}
                  {decimalToDMS(selectedPoint.longitude, false)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function decimalToDMS(decimal, isLat) {
  if (decimal == null) return null;
  const abs  = Math.abs(decimal);
  const deg  = Math.floor(abs);
  const mFull = (abs - deg) * 60;
  const min  = Math.floor(mFull);
  const sec  = (mFull - min) * 60;
  const hemi = isLat
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'O');
  return `${deg}° ${String(min).padStart(2, '0')}' ${sec.toFixed(4)}" ${hemi}`;
}

function Detail({ label, value, mono, warn }) {
  return (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 13, color: warn ? C.amber : (value ? C.text : C.dim),
        fontFamily: mono ? 'monospace' : 'inherit',
      }}>
        {value ?? <span style={{ color: C.dim }}>—</span>}
      </div>
    </div>
  );
}

/* ── Excel Tab (unchanged logic, kept) ──────────────────────── */
const EXCEL_FIELDS = [
  { key: 'nom',       label: 'Nom du point',   required: true  },
  { key: 'latitude',  label: 'Latitude',        required: true  },
  { key: 'longitude', label: 'Longitude',       required: true  },
  { key: 'laborde_x', label: 'X Laborde',       required: true  },
  { key: 'laborde_y', label: 'Y Laborde',       required: true  },
  { key: 'ordre',     label: 'Ordre (R0, R1…)', required: false },
  { key: 'precision', label: 'Précision',       required: false },
];

function ExcelTab({ onImportDone }) {
  const [file,    setFile]    = useState(null);
  const [columns, setColumns] = useState([]);
  const [preview, setPreview] = useState([]);
  const [mapping, setMapping] = useState({});
  const [status,  setStatus]  = useState('idle');
  const [result,  setResult]  = useState(null);
  const [message, setMessage] = useState('');

  const reset = () => {
    setFile(null); setColumns([]); setPreview([]);
    setMapping({}); setStatus('idle'); setResult(null); setMessage('');
  };

  const analyze = async (f) => {
    setFile(f); setStatus('analyzing');
    try {
      const fd = new FormData(); fd.append('data_file', f);
      const { data } = await filesApi.excelAnalyze(fd);
      setColumns(data.data?.columns || []);
      setPreview(data.data?.data    || []);
      setStatus('mapping');
    } catch (e) {
      setMessage(e.response?.data?.message || "Erreur d'analyse."); setStatus('error');
    }
  };

  const canSave = EXCEL_FIELDS.filter(f => f.required).every(f => mapping[f.key]);

  const save = async () => {
    setStatus('saving');
    try {
      const fd = new FormData();
      fd.append('data_file', file);
      fd.append('attribute_mapping', JSON.stringify(mapping));
      const { data } = await filesApi.excelSave(fd);
      setResult(data.data); setMessage(data.message || ''); setStatus('done');
      if ((data.data?.saved_count || 0) > 0) onImportDone?.();
    } catch (e) {
      setMessage(e.response?.data?.message || "Erreur d'enregistrement."); setStatus('error');
    }
  };

  if (status === 'idle' || status === 'error') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DropZone accept=".xlsx,.xls,.csv" onFile={analyze}
        label={file ? file.name : 'Déposer un fichier Excel ou CSV'} />
      {status === 'error' && <Alert type="error">{message}</Alert>}
    </div>
  );

  if (status === 'analyzing') return <Loader msg="Analyse du fichier…" />;
  if (status === 'saving')    return <Loader msg="Importation en cours…" />;

  if (status === 'mapping') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>
        Associer les colonnes du fichier aux champs attendus
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {EXCEL_FIELDS.map(f => (
          <div key={f.key} style={{
            background: C.surface2, borderRadius: 8, padding: '10px 14px',
            border: `1px solid ${mapping[f.key] ? C.borderDk : C.border}`,
          }}>
            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 6 }}>
              {f.label}{f.required && <span style={{ color: C.red, marginLeft: 4 }}>*</span>}
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={mapping[f.key] || ''}
                onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                style={{
                  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
                  color: C.text, borderRadius: 6, padding: '5px 28px 5px 10px',
                  fontSize: 12, appearance: 'none', cursor: 'pointer',
                }}
              >
                <option value="">— Sélectionner —</option>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={12} color={C.muted} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none',
              }} />
            </div>
          </div>
        ))}
      </div>

      {preview.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.a06 }}>
                {columns.map(c => (
                  <th key={c} style={{
                    padding: '6px 10px', color: C.muted, fontWeight: 600, textAlign: 'left',
                    borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                  }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.slice(0, 5).map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  {columns.map(c => (
                    <td key={c} style={{ padding: '5px 10px', color: C.text, whiteSpace: 'nowrap' }}>
                      {row[c] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Annuler</Btn>
        <Btn onClick={save} disabled={!canSave}><Save size={14} /> Importer</Btn>
      </div>
    </div>
  );

  if (status === 'done') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Alert type="success">{message}</Alert>
      {result?.ignored_count > 0 && (
        <Alert type="warning">
          {result.ignored_count} ligne(s) ignorée(s).
          {result.ignored_rows?.slice(0, 3).map((r, i) => (
            <div key={i} style={{ fontSize: 11, marginTop: 4 }}>
              Ligne {r.row}: {r.errors?.join(', ')}
            </div>
          ))}
        </Alert>
      )}
      <Btn variant="ghost" onClick={reset}><RotateCcw size={14} /> Importer un autre fichier</Btn>
    </div>
  );

  return null;
}

/* ── Inventory Tab ───────────────────────────────────────────── */
function InventoryTab({ refreshKey }) {
  const [points,          setPoints]          = useState([]);
  const [total,           setTotal]           = useState(0);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState('');
  const [expandId,        setExpandId]        = useState(null);
  const [editPt,          setEditPt]          = useState(null);
  const [deletePt,        setDeletePt]        = useState(null);
  const [sortCol,         setSortCol]         = useState('nom');
  const [sortAsc,         setSortAsc]         = useState(true);
  const [selectedIds,     setSelectedIds]     = useState(new Set());
  const [bulkDeleteMode,  setBulkDeleteMode]  = useState(null); // 'selection' | 'all'
  const selectAllRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await geodesieApi.list({ size: 500 });
      const pts = (data?.data?.features || []).map(normalizeGeoPoint);
      setPoints(pts);
      setTotal(data?.meta?.count ?? pts.length);
    } catch (e) {
      console.error('Erreur chargement inventaire :', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  /* ── Tri + filtre ── */
  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? points.filter(p =>
          p.nom?.toLowerCase().includes(q) ||
          p.id_point?.toLowerCase().includes(q))
      : [...points];

    list.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      return sortAsc ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return list;
  }, [points, search, sortCol, sortAsc]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(true); }
  };

  /* ── Edit save ── */
  const handleSave = async (form) => {
    const { data } = await geodesieApi.update(editPt.id, {
      nom:      form.nom.trim(),
      id_point: form.id_point.trim(),
      ordre:     form.ordre     !== '' ? parseInt(form.ordre,     10) : null,
      precision: form.precision !== '' ? parseInt(form.precision, 10) : null,
    });
    const updated = normalizeGeoPoint(data);
    setPoints(prev => prev.map(p => p.id === updated.id ? updated : p));
    setEditPt(null);
  };

  /* ── Delete (single) ── */
  const handleDelete = async () => {
    await geodesieApi.remove(deletePt.id);
    setPoints(prev => prev.filter(p => p.id !== deletePt.id));
    setTotal(t => Math.max(0, t - 1));
    if (expandId === deletePt.id) setExpandId(null);
    setDeletePt(null);
  };

  /* ── Delete (bulk) ── */
  const handleBulkDelete = async () => {
    const ids = bulkDeleteMode === 'all'
      ? displayed.map(p => p.id)
      : [...selectedIds];
    await geodesieApi.removeMany(ids);
    const idSet = new Set(ids);
    setPoints(prev => prev.filter(p => !idSet.has(p.id)));
    setTotal(t => Math.max(0, t - ids.length));
    if (expandId && idSet.has(expandId)) setExpandId(null);
    setSelectedIds(new Set());
    setBulkDeleteMode(null);
  };

  /* ── Select-all indeterminate ── */
  const allDisplayedSelected = displayed.length > 0 && displayed.every(p => selectedIds.has(p.id));
  const someSelected         = selectedIds.size > 0 && !allDisplayedSelected;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  /* ── Sortable header ── */
  const Th = ({ col, label, right }) => {
    const active = sortCol === col;
    return (
      <th
        onClick={() => toggleSort(col)}
        style={{
          padding: '8px 10px', textAlign: right ? 'right' : 'left',
          color: active ? C.green : C.muted,
          fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
          borderBottom: `1px solid ${C.border}`,
          cursor: 'pointer', userSelect: 'none',
          background: C.a05,
        }}
      >
        {label}
        {active && <span style={{ marginLeft: 4 }}>{sortAsc ? '↑' : '↓'}</span>}
      </th>
    );
  };

  /* ── Row detail (expanded) ── */
  const ExpandedRow = ({ pt }) => (
    <tr>
      <td colSpan={12} style={{ padding: '0 0 0 28px', background: C.a04 }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 20, padding: '16px 16px 16px 8px',
          borderLeft: `3px solid ${C.green}`,
        }}>
          {/* Laborde */}
          <section>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>LABORDE</div>
            <InfoRow label="X"     value={pt.x_laborde?.toFixed(3)} mono />
            <InfoRow label="Y"     value={pt.y_laborde?.toFixed(3)} mono />
          </section>

          {/* WGS84 décimal */}
          <section>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>WGS 84 — Décimal</div>
            <InfoRow label="Latitude"  value={pt.latitude?.toFixed(8)} mono />
            <InfoRow label="Longitude" value={pt.longitude?.toFixed(8)} mono />
          </section>

          {/* WGS84 DMS */}
          <section>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>WGS 84 — DMS</div>
            <InfoRow label="Latitude"  value={decimalToDMS(pt.latitude,  true)} mono green />
            <InfoRow label="Longitude" value={decimalToDMS(pt.longitude, false)} mono green />
          </section>

          {/* Méta */}
          <section>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>MÉTADONNÉES</div>
            <InfoRow label="Code"      value={pt.id_point} />
            <InfoRow label="Ordre"     value={pt.ordre != null ? `R${pt.ordre}` : null} />
            <InfoRow label="Précision" value={pt.precision != null ? `${pt.precision} cm` : null} />
            <InfoRow label="Ajouté le" value={pt.date_ajout ? new Date(pt.date_ajout).toLocaleDateString('fr-FR') : null} />
          </section>
        </div>
      </td>
    </tr>
  );

  /* ── Edit modal ── */
  const EditModal = () => {
    const [form, setForm] = useState({
      nom:       editPt.nom       ?? '',
      id_point:  editPt.id_point  ?? '',
      ordre:     editPt.ordre     != null ? String(editPt.ordre)     : '',
      precision: editPt.precision != null ? String(editPt.precision) : '',
    });
    const [busy, setBusy] = useState(false);
    const [err,  setErr]  = useState('');

    const field = (k, label, type = 'text') => (
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
        <input
          type={type}
          value={form[k]}
          onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
          style={{
            width: '100%', background: C.bg80,
            border: `1px solid ${C.borderDk}`, color: C.text,
            borderRadius: 6, padding: '7px 10px', fontSize: 13,
            boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
          }}
        />
      </div>
    );

    const submit = async () => {
      if (!form.nom.trim()) { setErr('Le nom est requis.'); return; }
      setBusy(true); setErr('');
      try { await handleSave(form); }
      catch { setErr('Erreur lors de la sauvegarde.'); setBusy(false); }
    };

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: C.overlay, backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={e => e.target === e.currentTarget && setEditPt(null)}>
        <div style={{
          background: C.surface, border: `1px solid ${C.borderDk}`,
          borderRadius: 14, padding: '24px 28px', width: 430, maxWidth: '92vw',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 20 }}>
            <Edit3 size={15} color={C.green} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Modifier — {editPt.nom}</span>
            <button onClick={() => setEditPt(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 4 }}>
              <X size={16} />
            </button>
          </div>

          {field('nom',      'Nom')}
          {field('id_point', 'Code / ID point')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>{field('ordre',     'Ordre (0 = R0…)', 'number')}</div>
            <div>{field('precision', 'Précision (cm)',   'number')}</div>
          </div>

          {/* Coords read-only */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>
              COORDONNÉES — lecture seule
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11, fontFamily: 'monospace', color: C.muted }}>
              <div>Lat : {editPt.latitude?.toFixed(6) ?? '—'}</div>
              <div>Lon : {editPt.longitude?.toFixed(6) ?? '—'}</div>
              <div>X Lab. : {editPt.x_laborde?.toFixed(2) ?? '—'}</div>
              <div>Y Lab. : {editPt.y_laborde?.toFixed(2) ?? '—'}</div>
            </div>
          </div>

          {err && <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <Btn variant="ghost" onClick={() => setEditPt(null)}>Annuler</Btn>
            <Btn onClick={submit} disabled={busy}>
              <Save size={13} /> {busy ? 'Sauvegarde…' : 'Enregistrer'}
            </Btn>
          </div>
        </div>
      </div>
    );
  };

  /* ── Delete confirm ── */
  const DeleteConfirm = () => {
    const [busy, setBusy] = useState(false);
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: C.overlay, backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={e => e.target === e.currentTarget && setDeletePt(null)}>
        <div style={{
          background: C.surface, border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 14, padding: '24px 28px', width: 380, maxWidth: '92vw',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <Trash2 size={15} color={C.red} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Supprimer le point</span>
          </div>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 20px' }}>
            Supprimer définitivement <strong style={{ color: C.text }}>{deletePt.nom}</strong>
            {deletePt.id_point ? ` (${deletePt.id_point})` : ''} ? Action irréversible.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setDeletePt(null)}>Annuler</Btn>
            <Btn variant="danger" disabled={busy} onClick={async () => { setBusy(true); await handleDelete(); }}>
              <Trash2 size={13} /> {busy ? 'Suppression…' : 'Supprimer'}
            </Btn>
          </div>
        </div>
      </div>
    );
  };

  /* ── Bulk delete confirm ── */
  const BulkDeleteConfirm = () => {
    const [busy, setBusy] = useState(false);
    const ids   = bulkDeleteMode === 'all' ? displayed.map(p => p.id) : [...selectedIds];
    const count = ids.length;
    const label = bulkDeleteMode === 'all'
      ? `Supprimer les ${count} point(s)${search ? ' correspondant à la recherche' : ''} ?`
      : `Supprimer ${count} point(s) sélectionné(s) ?`;
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: C.overlay, backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={e => e.target === e.currentTarget && setBulkDeleteMode(null)}>
        <div style={{
          background: C.surface, border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 14, padding: '24px 28px', width: 400, maxWidth: '92vw',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <Trash2 size={15} color={C.red} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Suppression groupée</span>
            <button onClick={() => setBulkDeleteMode(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 4 }}>
              <X size={16} />
            </button>
          </div>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 20px' }}>
            {label} <strong style={{ color: C.red }}>Action irréversible.</strong>
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setBulkDeleteMode(null)}>Annuler</Btn>
            <Btn variant="danger" disabled={busy} onClick={async () => { setBusy(true); await handleBulkDelete(); }}>
              <Trash2 size={13} /> {busy ? 'Suppression…' : `Supprimer ${count}`}
            </Btn>
          </div>
        </div>
      </div>
    );
  };

  /* ── Render ── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {editPt          && <EditModal />}
      {deletePt        && <DeleteConfirm />}
      {bulkDeleteMode  && <BulkDeleteConfirm />}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 360 }}>
          <Search size={13} color={C.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Rechercher par nom ou code…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', background: C.bg60, border: `1px solid ${C.border}`,
              color: C.text, borderRadius: 8, padding: '7px 10px 7px 32px',
              fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 2 }}>
              <X size={13} />
            </button>
          )}
        </div>

        <span style={{ fontSize: 12, color: C.muted }}>
          {loading ? 'Chargement…' : `${displayed.length} / ${total} point(s)`}
        </span>

        <Btn variant="ghost" onClick={load} style={{ padding: '7px 12px' }}>
          <RefreshCw size={13} style={{ animation: loading ? 'ag-slide 1.4s linear infinite' : 'none' }} />
          Actualiser
        </Btn>

        {selectedIds.size > 0 && (
          <Btn variant="danger" onClick={() => setBulkDeleteMode('selection')} style={{ padding: '6px 12px', fontSize: 12 }}>
            <Trash2 size={12} /> Supprimer la sélection ({selectedIds.size})
          </Btn>
        )}
        {!loading && displayed.length > 0 && (
          <Btn variant="danger" onClick={() => setBulkDeleteMode('all')} style={{ padding: '6px 12px', fontSize: 12, opacity: 0.65 }}>
            <Trash2 size={12} /> Tout supprimer ({displayed.length})
          </Btn>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <Loader msg="Chargement de l'inventaire…" />
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: C.muted, fontSize: 13 }}>
          {search ? 'Aucun point ne correspond à la recherche.' : 'Aucun point géodésique enregistré.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {/* Select-all */}
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, background: C.a05, width: 36, textAlign: 'center' }}>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allDisplayedSelected}
                    onChange={() => {
                      if (allDisplayedSelected) setSelectedIds(new Set());
                      else setSelectedIds(new Set(displayed.map(p => p.id)));
                    }}
                    style={{ cursor: 'pointer', accentColor: C.green }}
                  />
                </th>
                <Th col="nom"        label="Nom"       />
                <Th col="id_point"   label="Code"      />
                <Th col="ordre"      label="Ordre"     />
                <Th col="x_laborde"  label="X Laborde" right />
                <Th col="y_laborde"  label="Y Laborde" right />
                <Th col="latitude"   label="Latitude"  right />
                <Th col="longitude"  label="Longitude" right />
                <Th col="precision"  label="Préc."     />
                <Th col="date_ajout" label="Ajouté"    />
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, background: C.a05, width: 36, textAlign: 'center' }} title="Fiche PDF">
                  <FileText size={11} color={C.muted} />
                </th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, background: C.a05, width: 70 }} />
              </tr>
            </thead>
            <tbody>
              {displayed.map(pt => {
                const expanded  = pt.id === expandId;
                const isChecked = selectedIds.has(pt.id);
                return (
                  <React.Fragment key={pt.id}>
                    <tr
                      onClick={() => setExpandId(expanded ? null : pt.id)}
                      style={{
                        borderBottom: expanded ? 'none' : `1px solid ${C.border}`,
                        cursor: 'pointer',
                        background: expanded ? C.a07 : isChecked ? C.a05 : 'transparent',
                        transition: 'background .1s',
                      }}
                      onMouseEnter={e => { if (!expanded && !isChecked) e.currentTarget.style.background = 'var(--c-a04)'; }}
                      onMouseLeave={e => { if (!expanded && !isChecked) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Checkbox */}
                      <td style={{ padding: '8px 10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(pt.id)) next.delete(pt.id); else next.add(pt.id);
                            return next;
                          })}
                          style={{ cursor: 'pointer', accentColor: C.green }}
                        />
                      </td>

                      {/* Nom */}
                      <td style={{ padding: '8px 10px', color: C.text, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {expanded ? <ChevronUp size={12} color={C.green} /> : <ChevronDown size={12} color={C.muted} />}
                          <MapPin size={10} color={C.green} />
                          {pt.nom}
                        </div>
                      </td>

                      {/* Code */}
                      <td style={{ padding: '8px 10px', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>
                        {pt.id_point || '—'}
                      </td>

                      {/* Ordre */}
                      <td style={{ padding: '8px 10px' }}>
                        {pt.ordre != null
                          ? <span style={{ background: C.a12, color: C.green, borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>R{pt.ordre}</span>
                          : <span style={{ color: C.dim }}>—</span>}
                      </td>

                      {/* X Laborde */}
                      <td style={{ padding: '8px 10px', color: C.text, fontFamily: 'monospace', fontSize: 11, textAlign: 'right' }}>
                        {pt.x_laborde != null ? pt.x_laborde.toFixed(2) : '—'}
                      </td>

                      {/* Y Laborde */}
                      <td style={{ padding: '8px 10px', color: C.text, fontFamily: 'monospace', fontSize: 11, textAlign: 'right' }}>
                        {pt.y_laborde != null ? pt.y_laborde.toFixed(2) : '—'}
                      </td>

                      {/* Latitude */}
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, textAlign: 'right', color: pt.latitude != null ? C.text : C.amber }}>
                        {pt.latitude != null ? pt.latitude.toFixed(6) : <span style={{ color: C.amber }}>—</span>}
                      </td>

                      {/* Longitude */}
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, textAlign: 'right', color: pt.longitude != null ? C.text : C.amber }}>
                        {pt.longitude != null ? pt.longitude.toFixed(6) : <span style={{ color: C.amber }}>—</span>}
                      </td>

                      {/* Précision */}
                      <td style={{ padding: '8px 10px', color: C.muted, fontSize: 11 }}>
                        {pt.precision != null ? `${pt.precision} cm` : '—'}
                      </td>

                      {/* Date */}
                      <td style={{ padding: '8px 10px', color: C.muted, fontSize: 11, whiteSpace: 'nowrap' }}>
                        {pt.date_ajout ? new Date(pt.date_ajout).toLocaleDateString('fr-FR') : '—'}
                      </td>

                      {/* PDF */}
                      <td style={{ padding: '8px 10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        {pt.pdf_url ? (
                          <a
                            href={MEDIA_BASE + pt.pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            title="Voir la fiche PDF"
                            style={{ color: C.green, display: 'inline-flex', alignItems: 'center' }}
                          >
                            <Download size={13} />
                          </a>
                        ) : (
                          <FileText size={13} color={C.dim} title="Pas de PDF" />
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '8px 10px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <ActionBtn icon={Edit3} color="#60a5fa" hoverBg="rgba(96,165,250,0.1)" title="Modifier" onClick={() => setEditPt(pt)} />
                          <ActionBtn icon={Trash2} color={C.red}  hoverBg="rgba(239,68,68,0.1)"  title="Supprimer" onClick={() => setDeletePt(pt)} />
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {expanded && <ExpandedRow pt={pt} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono, green }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5, gap: 8 }}>
      <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 11, fontFamily: mono ? 'monospace' : 'inherit',
        color: green ? C.green : (value ? C.text : C.dim),
        textAlign: 'right',
      }}>{value ?? '—'}</span>
    </div>
  );
}

function ActionBtn({ icon: Icon, color, hoverBg, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? hoverBg : 'none', border: 'none',
        padding: '4px 5px', borderRadius: 5, cursor: 'pointer',
        color, display: 'flex', alignItems: 'center', transition: 'background .12s',
      }}
    ><Icon size={12} /></button>
  );
}

function Loader({ msg }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 0', color: C.muted }}>
      <div style={{ fontSize: 13, marginBottom: 10 }}>{msg}</div>
      <div style={{ width: 200, height: 3, background: C.dim, borderRadius: 3, margin: '0 auto', overflow: 'hidden' }}>
        <div style={{ width: '55%', height: '100%', background: C.green, borderRadius: 3 }} />
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
export default function GeodesyManager() {
  const [tab,        setTab]        = useState('pdf');
  const [importCount, setImportCount] = useState(0); // incrémenté après chaque import réussi

  const handleImportDone = useCallback(() => {
    setImportCount(n => n + 1);
    setTab('inventory');
  }, []);

  return (
    <div style={{
      height: '100%', overflow: 'auto', padding: 28,
      background: C.bg, color: C.text,
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <style>{`
        @keyframes ag-slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Points géodésiques</div>
        <div style={{ fontSize: 13, color: C.muted }}>
          Importer depuis des fiches FTM (PDF, Word) ou des fichiers tabulaires — les coordonnées doivent être présentes dans les deux systèmes (Laborde + WGS84)
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}` }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 16px', background: 'none', border: 'none',
              borderBottom: `2px solid ${active ? C.green : 'transparent'}`,
              color: active ? C.green : C.muted, fontWeight: active ? 700 : 500,
              fontSize: 13, cursor: 'pointer', marginBottom: -1, transition: 'all .15s',
            }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
        {tab === 'pdf'       && <PdfTab   onImportDone={handleImportDone} />}
        {tab === 'excel'     && <ExcelTab  onImportDone={handleImportDone} />}
        {tab === 'inventory' && <InventoryTab refreshKey={importCount} />}
      </div>
    </div>
  );
}
